import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import Page from 'flarum/common/components/Page';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import Button from 'flarum/common/components/Button';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import WidgetRegistry from './WidgetRegistry';
import { registerBespokePanel } from './panelRegistry';
import { applyTokens } from '../common/tokens';
import BespokeLauncher from './components/BespokeLauncher';
import registerBuiltinWidgets from './widgets';
import enhanceDiscussionList from './enhanceList';
import cardMenu from './cardMenu';
import typingCards from './typingCards';
import { ensureZones, renderZones, cleanupZones, isPlaced } from './zones';
import { installSpaLinks } from './util/spaLinks';
import BespokeHeaderNav from './components/BespokeHeaderNav';
import BespokeCategories from './components/BespokeCategories';
import { fitIconsWhenReady } from './util/fitIcons';
import { mountChrome, applyTagColor, toggleDarkScheme, currentScheme } from './siteFeatures';
import { initReadability } from './readability';
import applyBackButton from './backButton';
import profile from './profile';
import markAllAsRead from './util/markAllRead';
import heroVideo from './heroVideo';

// Mithril is global in Flarum (`m`); declared for type-checking, resolved at
// runtime — avoids bundling a second copy.

/**
 * Forum entry point.
 *
 *  - Exposes `app.bespoke` (widget registry + the published document).
 *  - Mounts widget zones from the published layout.
 *  - For users with `bespoke.edit`, adds the floating "Edit theme" launcher.
 *
 * IMPORTANT: `app.forum` is NOT populated when initializers run — it is set
 * during boot AFTER initializers. So all forum-data access happens inside a
 * component lifecycle hook (IndexPage.oncreate), never at the top level of the
 * initializer. Reading it early throws and takes down the whole frontend.
 * (Note: `flarum/forum/ForumApplication` is not in the runtime module registry,
 * so it can't be imported/extended — that's why we hook the page, not mount().)
 *
 * The styling (CSS variables, fonts) is injected server-side by
 * InjectThemeVariables, so the forum is themed before JS runs — no flash. The
 * JS layer only adds widgets + the editor.
 */
app.initializers.add('ernestdefoe/bespoke', () => {
  // Widget links navigate in place, as Flarum's own do, instead of reloading the forum.
  installSpaLinks('[data-bespoke-zone]');

  // A video beside the welcome banner's words, when the Hero panel has one.
  heroVideo();

  /*
   * 🚨 Mark all as read, put back.
   *
   * Core has it — an icon button in IndexPage's action toolbar, beside the
   * discussion list. In category mode this theme REPLACES that list, and the
   * toolbar goes with it, so a reader on a board with a hundred and fifty
   * forums had no way at all to clear their unread marks. The feature was not
   * removed on purpose; it was collateral, which is the worst kind of missing.
   *
   * It goes in the sidebar rather than back in the toolbar because the sidebar
   * is the one thing present in every layout this theme offers, and because
   * this theme's header nav is built from that same ItemList — so it appears in
   * both without being added twice.
   */
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    if (!app.session.user) return;
    items.add('bespoke-markAllRead', Button.component({
      icon: 'fas fa-check',
      className: 'Button Button--link',
      onclick: markAllAsRead
    }, app.translator.trans('core.forum.index.mark_all_as_read_tooltip')),
    // Below the forums, above nothing: it is an action, not a destination.
    -100);
  });
  const registry = new WidgetRegistry();
  registerBuiltinWidgets(registry);

  // registerPanel: companion extensions add their own tab to the live editor
  // (they may also push onto window.BespokePanelQueue before we boot — the
  // editor drains that queue on render, so either path works at any time).
  /*
   * 🚨 `renderZones` is on here because a third-party widget has no other way
   * to redraw itself.
   *
   * Widgets are mounted with `m.render()` into their own roots, so a global
   * `m.redraw()` never reaches them — this file's own built-ins say exactly
   * that and import `renderZones` directly to get around it. An extension
   * cannot: it fetches, sets its state, calls `m.redraw()` as any Mithril
   * component would, and nothing happens. Game Day's scoreboard sat blank on
   * every first page load for precisely this reason, and it looked like a bug
   * in the scoreboard.
   */
  app.bespoke = {
    widgets: registry,
    document: null,
    registerPanel: registerBespokePanel,
    renderZones
  };

  // Mount the announcement bar + footer (they render from the published doc).
  mountChrome();

  // Long-form reading aids: progress bar, back-to-top, auto table of contents.
  initReadability();

  // Reading a post: make the header back arrow always return to the
  // discussion list (/all), in every list layout — not the Page Builder home.
  applyBackButton();

  // Decorate discussion rows (cover images, participant avatars, category
  // colouring) according to the active layout mode.
  enhanceDiscussionList();

  // Member profiles: post/discussion/solution counts in the header, so the
  // card says what somebody has done here and not only who they are.
  profile();

  // Grid cards: kebab menu with the standard discussion controls.
  cardMenu();

  // Live typing badges on list rows/cards (no-op without flarum/realtime).
  typingCards();

  // Tag-driven colouring on tag/discussion pages (ramon/colored replacement).
  extend(DiscussionPage.prototype, 'oncreate', applyTagColor);
  extend(DiscussionPage.prototype, 'onupdate', applyTagColor);

  // View counter: one hit per discussion per browser session (grid cards
  // show the total). Fire-and-forget; sessionStorage throttles repeats.
  extend(DiscussionPage.prototype, 'oncreate', function () {
    const id = m.route.param('id');
    if (!id || !/^\d+$/.test(String(id).match(/\d+/)?.[0] ?? '')) return;
    const num = String(id).match(/\d+/)[0];
    const key = 'bespoke-viewed-' + num;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) {
      /* storage unavailable — count anyway */
    }
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/bespoke/discussions/' + num + '/view'
    }).catch(() => {});
  });

  /*
   * Optional user dark-mode toggle in the header.
   *
   * 🚨 Only where core has not already put one there. Flarum 2 rc.8 ships its
   * own `themeSwitcher` — a dropdown with light, dark AND auto — so a site that
   * had this switched on got two controls for one setting sitting side by side,
   * one of them worse. Core's wins: it is the one that knows about `auto`, and
   * a reader who picks a theme in either expects the other to agree.
   *
   * Checked on the ItemList rather than by core version, so this stands down
   * for anything else that provides a switcher and comes back on its own if
   * core ever drops it.
   */
  extend(HeaderSecondary.prototype, 'items', function (items) {
    try {
      if (!app.bespoke.document?.tokens?.layout?.darkToggle) return;
      if (items.has('themeSwitcher')) return;
      items.add('bespokeDark', Button.component({
        className: 'Button Button--link',
        icon: currentScheme() === 'dark' ? 'fas fa-sun' : 'fas fa-moon',
        onclick: () => toggleDarkScheme()
      }), 15);
    } catch (e) {
      console.warn('[bespoke]', e);
    }
  });

  // Render the horizontal nav (New Discussion + nav pills) at the top of the
  // index content via Mithril — the canonical, robust pattern (the default
  // sidebar nav is hidden on desktop via CSS). contentItems() becomes
  // .Page-content; high priority puts the nav first.
  extend(IndexPage.prototype, 'contentItems', function (items) {
    // The bar gives up whatever the layout has placed as a widget instead:
    // the Navigation widget takes the pills, Start a discussion the button.
    items.add('bespoke-nav', BespokeHeaderNav.component({
      pills: !isPlaced('nav'),
      button: !isPlaced('compose')
    }), 200);

    // CATEGORY mode on the home → show a traditional category index instead of
    // the flat discussion list (inspired by fof/categories). 🚨 Only the plain
    // home: a tag, a search, or another extension's list on IndexPage (Keyhole's
    // /link-only, Following…) must keep its discussion list.
    let onHome = false;
    try {
      const params = app.search?.state?.params?.() || {};
      onHome = app.current.get('routeName') === 'index' && !params.tags && !params.q;
    } catch (e) {}
    const hasTags = (() => {
      try {
        return (app.store.all('tags') || []).length > 0;
      } catch (e) {
        return false;
      }
    })();
    if (document.documentElement.getAttribute('data-bespoke-discussions') === 'category' && onHome && hasTags) {
      items.remove('discussionList'); // swap the flat list…
      items.add('bespoke-categories', BespokeCategories.component(), 80); // …for the category index
    }
  });

  // Everything that needs forum data runs in a page lifecycle hook, never at
  // the top of the initializer (app.forum isn't populated yet there). IndexPage
  // is the natural home for the theme editor; the launcher persists across SPA
  // navigation once attached. All wrapped defensively so a hiccup can't break
  // the page.
  const setup = function () {
    try {
      // Non-dismissible hero → make sure Flarum's hide-flag is cleared so it shows.
      if (document.documentElement.getAttribute('data-bespoke-hero-dismiss') === 'off') {
        try {
          if (localStorage.getItem('welcomeHidden')) localStorage.removeItem('welcomeHidden');
        } catch (e) {}
      }
      ensureZones();

      // Only adopt the live doc when not actively editing (the editor sets
      // app.bespoke.document to its draft so the page mirrors live edits). The
      // member's personal "My Theme" wins over the global published default for
      // widget/layout rendering (CSS vars + data-attrs are resolved server-side).
      if (!document.body.classList.contains('bespoke-editing')) {
        const personal = app.forum?.attribute('bespokePersonal');
        const published = app.forum?.attribute('bespoke.published');
        app.bespoke.document = personal || published || null;
      }
      renderZones();
      applyTagColor();

      // Gate the editor on permission — global editors OR members who may
      // personalise their own view. Visitors never load it. (Visibility per
      // route is handled by the Page.oncreate hook below; this just ensures the
      // button exists once we're on a themed page.)
      BespokeLauncher.refresh('index');
    } catch (e) {
      console.error('[bespoke] setup failed', e);
    }
  };

  // Run on first render AND after every redraw — Mithril wipes our appended
  // zones when it re-renders the page, so we re-render widgets each time or they
  // vanish until the editor runs. (The nav is a real Mithril component now.)
  extend(IndexPage.prototype, 'oncreate', setup);
  extend(IndexPage.prototype, 'onupdate', function () {
    try {
      renderZones();
    } catch (e) {
      console.warn('[bespoke]', e);
    }
    // The active tag can resolve lazily (app.currentTag fetches then redraws),
    // so re-apply the tag colouring on each redraw, not just on create.
    try {
      applyTagColor();
    } catch (e) {
      console.warn('[bespoke]', e);
    }
  });
  // Leaving the index — strip the widget zones so they don't linger in the
  // discussion view next to the post scrubber.
  extend(IndexPage.prototype, 'onremove', function () {
    try {
      cleanupZones();
    } catch (e) {
      console.warn('[bespoke]', e);
    }
  });

  // Route-aware launcher visibility. Every page subclass calls super.oncreate(),
  // so hooking the base Page lets us toggle the floating "Edit theme" button on
  // each navigation: hidden on Page Builder pages (their own editor owns them),
  // shown on Bespoke-themed pages. routeName is set on the page's attrs.
  extend(Page.prototype, 'oncreate', function () {
    try {
      BespokeLauncher.refresh(this.attrs?.routeName);
    } catch (e) {
      /* never break a page render */
    }
    try {
      enhanceTagChildren();
    } catch (e) {
      /* never break a page render */
    }
    /* A Kit logo glyph can be several times wider than the chip it sits in and
     * CSS cannot measure it — see util/fitIcons. Runs after the children are
     * enhanced, because that is what puts the icons in the chips. */
    try {
      fitTagIcons();
    } catch (e) {}
    try {
      relocateAuxNav();
    } catch (e) {
      /* never break a page render */
    }
  });
  // Tags load async (the page redraws once they arrive), so re-run on update too.
  extend(Page.prototype, 'onupdate', function () {
    try {
      enhanceTagChildren();
    } catch (e) {}
    try {
      fitTagIcons();
    } catch (e) {}
    try {
      relocateAuxNav();
    } catch (e) {}
  });
});

/**
 * On IndexPage-based pages that AREN'T the real discussions index (which uses
 * BespokeHeaderNav's top pill bar) but bring their OWN sidebar nav — e.g.
 * linkrobins/support's ticket filters — move that nav to a horizontal bar at the
 * top of the content. This matches the index layout and frees the sidebar column
 * for widgets (so they don't sit on top of the nav). Idempotent + redraw-safe:
 * once moved, the sidebar selector no longer matches; if Mithril rebuilds the
 * sidebar nav, the next pass re-homes it (replaceChildren de-dupes).
 */
function relocateAuxNav() {
  // (a) IndexPage-based pages with a right-hand sidebar nav (e.g. linkrobins/
  //     support): MOVE the nav to a top pill bar so the sidebar is free for
  //     widgets. Skips the real discussions index (it has BespokeHeaderNav).
  const idx = document.querySelector('.IndexPage');
  if (idx && !idx.querySelector('.BespokeHeaderNav')) {
    const content = idx.querySelector('.Page-content');
    const sidebarNav = idx.querySelector('.Page-sidebar nav.IndexPage-nav');
    if (content && sidebarNav) {
      let wrap = content.querySelector(':scope > .Bespoke-auxNav');
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.className = 'Bespoke-auxNav';
        content.insertBefore(wrap, content.firstChild);
      }
      wrap.replaceChildren(sidebarNav);
    }
  }

  // (b) Tags page (vertical layout — its sidebar is already a full-width strip at
  //     the top, where the raw nav renders as a cramped row). Style it in place
  //     by tagging the sidebar with .Bespoke-auxNav so the same pill CSS applies.
  const tagsNav = document.querySelector('.TagsPage .Page-sidebar nav.IndexPage-nav');
  const tagsHost = tagsNav && tagsNav.closest('.Page-sidebar');
  if (tagsHost) tagsHost.classList.add('Bespoke-auxNav');
}

/**
 * On the /tags page, Flarum renders each tile's child tags as plain text links
 * (no icon, no colour). Enhance them in place with their FA icon + colour from
 * the store so the child chips match the rest of the tag UI. Idempotent.
 */
/** Both places the tags page draws an icon: the tile chip and the child pills. */
function fitTagIcons() {
  // 🚨 The chip on a tile IS the .icon element — its plate is drawn on that
  // element, not on a wrapper. Passing .TagTile-heading measured the glyph
  // against the 428px heading row, found it fitted, and scaled nothing.
  // grow: the tile chip widens to hold a wordmark (see sizeChip).
  fitIconsWhenReady('.TagsPage .TagTile-heading .icon', 0.82, true);
  fitIconsWhenReady('.TagsPage .TagTile-children a', 0.95);
}
function enhanceTagChildren() {
  const links = document.querySelectorAll('.TagsPage .TagTile-children a');
  if (!links.length) return;
  const bySlug = {};
  app.store.all('tags').forEach(t => {
    if (t && typeof t.slug === 'function' && t.slug()) bySlug[t.slug()] = t;
  });
  links.forEach(a => {
    if (a.dataset.bespokeTagChild) return;
    const m = (a.getAttribute('href') || '').match(/\/t\/([^/?#]+)/);
    if (!m) return;
    const tag = bySlug[decodeURIComponent(m[1])];
    if (!tag) return;
    const color = tag.color && tag.color();
    const icon = tag.icon && tag.icon();
    if (color) a.style.setProperty('--child-bg', color);
    if (icon && !a.querySelector('i.icon')) {
      const i = document.createElement('i');
      i.className = 'icon ' + icon;
      a.insertBefore(i, a.firstChild);
    }
    a.dataset.bespokeTagChild = '1';
  });
}

// zone plumbing now lives in ./zones (decoupled to avoid a circular import)
export { renderZones } from './zones';
export { applyTokens, WidgetRegistry };